import deepmerge from 'deepmerge';

import {
  ADDRESS_FIELDS_TO_SEND,
  APPLICANT_FIELDS_TO_SEND,
  CHILD_FIELDS_TO_SEND,
  DOCUMENT_FIELDS_TO_SEND,
  MARRIAGE_FIELDS_TO_SEND,
  ORGANIZATION_FIELDS_TO_SEND,
  PERSON_FIELDS_TO_SEND,
  SECOND_PARENT_FIELDS_TO_SEND,
} from '../constants/fieldNamesToSend';

import {
  formatDateFromDraft,
  isAdult,
  shouldHaveIdentification,
} from './dateHelpers';
import {
  CHANGE_NAME_DOC_ISSUER,
  CHANGED_FIO_FIELD_NAME,
  IS_CHANGED_PATRONYMIC_ABSENT,
  MARRIAGE_INFO_FIELD_NAME,
  ORGANIZATION_FIELD_NAME,
  PERSON_FIELD_NAME,
} from '../constants/fieldNames';
import {
  BORN_IN,
  BORN_IN_MARRIAGE,
  CHANGE_NAME,
  CHILD_ADDRESS,
} from '../constants/child';
import {
  DEAD_PARENT,
  SECOND_PARENT_STATUS,
} from '../constants/secondParentStatus';
import {
  CITIZENSHIP,
  DOCUMENT_TYPE,
  DocumentFields,
  documentInitialState,
  dulInitialState,
} from '../constants/document';

import { initialAddressInfo } from '../models/address';

import { AddressFieldsForSubmit, MarriageForSubmit } from '../models/submit';

import {
  ADDRESS_FIELDS_FROM_DRAFT,
  DOCUMENT_FIELDS_FROM_DRAFT,
  MARRIAGE_FIELDS_FROM_DRAFT,
  PERSON_FIELDS_FROM_DRAFT,
} from '../constants/fieldNamesFromDraft';
import { serializePhoneValue } from '../constants/REGEXP_PATTERNS';
import { ApplicantFields } from '../models/applicant';
import { LIVING_TOGETHER, MARITAL_STATUS } from '../constants/applicant';
import { ADDRESS_MATCH } from '../constants/spouse';
import {
  ADDRESS_INFO_FIELD_NAME,
  areaSuggestions,
  FIAS_LEVEL_HOUSE,
  isNewMoscowArea,
} from '../constants/address';
import { checkAddress, getDistricts } from './address';

import getFullName from './getFullName';
import { getId } from './getIds';
import { cardInitialState } from '../models/card';
import {
  initialPersonState,
  initialSecondParentState,
  initialOrganizationState,
  Organization,
} from './initialState';
import { getTextValue } from '../constants/getFieldValues';
import { AddressFields } from '../../types/address';

const personalInfoFields = Object.keys(PERSON_FIELDS_FROM_DRAFT);
const applicantFields = Object.values(APPLICANT_FIELDS_TO_SEND);
const addressFields = Object.keys(ADDRESS_FIELDS_FROM_DRAFT);
const childFields = [...Object.values(CHILD_FIELDS_TO_SEND)];
const documentFields = Object.keys(DOCUMENT_FIELDS_FROM_DRAFT);

function isPersonalInfoField(key) {
  return personalInfoFields.includes(key);
}

function isApplicantFields(key) {
  return applicantFields.includes(key);
}

function isAddressFields(key) {
  return addressFields.includes(key);
}

function isChildFields(key) {
  return childFields.includes(key);
}

function isDocumentFields(key) {
  return documentFields.includes(key);
}

type AddressFromDraft = Partial<AddressFields> | null;

const setValue = (value): ReturnType<typeof value> => ({ value });

const getAddressString = (
  addressFields: Partial<AddressFieldsForSubmit>
): string => {
  const additionalName = {
    region: 'Город',
    ownership: 'Владение',
    structure: 'Строение',
    building: 'Корпус',
    house: 'Дом',
  };
  const Moscow = 'Москва';
  const totalAddressString = Object.keys(addressFields).reduce(
    (addressString, key) => {
      if (
        [
          ADDRESS_FIELDS_FROM_DRAFT.area,
          ADDRESS_FIELDS_FROM_DRAFT.district,
          ADDRESS_FIELDS_FROM_DRAFT.flat,
          ADDRESS_FIELDS_TO_SEND.isManualInput,
          ADDRESS_FIELDS_TO_SEND.isServeByEirc,
          ADDRESS_FIELDS_TO_SEND.fiasId,
          ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant,
        ].includes(key)
      ) {
        return addressString;
      }
      const keyValue = addressFields[key];
      if (keyValue === Moscow) {
        return (addressString += `${additionalName[key]} ${keyValue},`);
      }
      if (keyValue) {
        return (addressString += ` ${
          additionalName[key] ? `${additionalName[key]} ${keyValue}` : keyValue
        }`);
      }
      return addressString;
    },
    ''
  );
  return totalAddressString === `${additionalName.region} ${Moscow},`
    ? ''
    : totalAddressString;
};

export const addressFromDraft: (
  addressFields?: AddressFieldsForSubmit
) => AddressFromDraft = (addressFields: AddressFieldsForSubmit) => {
  if (!addressFields) return null;

  const {
    omk_district_code,
    omk_area_code,
    omk_street_code,
    bti_building_code,
    fias_id,
    is_manual_input: isManualInput,
    ...restFields
  } = addressFields;

  const address = setValue(isManualInput ? '' : getAddressString(restFields));
  const getFiasLevel = ({ building, structure, house, ownership = '' }) => {
    return !isManualInput &&
      [building, structure, house, ownership].some(Boolean)
      ? FIAS_LEVEL_HOUSE
      : null;
  };

  const addressToForm = Object.keys(restFields).reduce(
    (next, key) => {
      if (!isAddressFields(key)) return next;
      const keyValue = addressFields[key];
      if (!keyValue && typeof keyValue !== 'boolean') return next;
      if (key === ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant) return next;

      if (key === ADDRESS_FIELDS_FROM_DRAFT.region) {
        return {
          ...next,
          region: setValue(keyValue),
        };
      }

      if (key === ADDRESS_FIELDS_FROM_DRAFT.area) {
        return {
          ...next,
          [ADDRESS_FIELDS_FROM_DRAFT[key]]: setValue(
            areaSuggestions.find(({ text }) => text === keyValue)
          ),
        };
      }

      if (key === ADDRESS_INFO_FIELD_NAME.district) {
        const districtValue = keyValue;
        if (isManualInput && !isNewMoscowArea(addressFields.area)) {
          const district = getDistricts(addressFields.area).find(({ text }) => {
            return text === districtValue || districtValue.includes(text);
          });
          return {
            ...next,
            [ADDRESS_FIELDS_FROM_DRAFT.district]: setValue(district),
          };
        }
        return {
          ...next,
          [ADDRESS_FIELDS_FROM_DRAFT.district]: setValue({
            text: districtValue,
          }),
        };
      }
      if (key === ADDRESS_FIELDS_TO_SEND.isServeByEirc) {
        return {
          ...next,
          isServeByEirc: typeof keyValue === 'boolean' ? keyValue : null,
        };
      }
      return {
        ...next,
        [ADDRESS_FIELDS_FROM_DRAFT[key]]: setValue(keyValue),
      };
    },
    {
      ...initialAddressInfo,
      omk_district_code,
      omk_area_code,
      omk_street_code,
      bti_building_code,
      isManualInput,
      address,
      fiasLevel: getFiasLevel(restFields),

      fiasId: fias_id || null,
    }
  ) as AddressFields;
  addressToForm.isAddressCompleted =
    addressToForm.isManualInput || checkAddress(addressToForm);
  addressToForm.street.isFoundInCatalog =
    addressToForm.isManualInput &&
    !!addressToForm.street.value &&
    !isNewMoscowArea(getTextValue(addressToForm.area));
  return addressToForm;
};

const patronymicFromDraft = (fieldsObject, name, isEmptyName) => {
  const isPatronymicAbsent =
    fieldsObject[PERSON_FIELDS_TO_SEND.isPatronymicAbsent];
  const fieldValue = fieldsObject[PERSON_FIELDS_TO_SEND['patronymic']] || '';
  const patronymic = isPatronymicAbsent ? 'Нет отчества' : fieldValue;
  return {
    [name]: setValue(patronymic),
    [isEmptyName]: setValue(isPatronymicAbsent),
  };
};

const birthPlaceFromDraft = (dulFields) => {
  if (dulFields && dulFields[DOCUMENT_FIELDS_TO_SEND.birthPlace]) {
    return setValue(dulFields[DOCUMENT_FIELDS_TO_SEND.birthPlace]);
  }
  return { value: '' };
};

const citizenshipFromDraft = (dulFields) => {
  if (dulFields && dulFields.citizenship) {
    return setValue(CITIZENSHIP[dulFields.citizenship]);
  }
  return { value: null };
};

export const personFromDraft = (personFields) => {
  const patronymic = patronymicFromDraft(
    personFields,
    PERSON_FIELD_NAME.patronymic,
    PERSON_FIELD_NAME.isPatronymicAbsent
  );
  const getType = (value) => {
    if (typeof value !== 'boolean') return null;
    return value ? CHANGE_NAME.notMatch : CHANGE_NAME.match;
  };
  const changeName = getType(personFields[PERSON_FIELDS_TO_SEND.isNameChanged]);

  return Object.keys(personFields)
    .filter(isPersonalInfoField)
    .reduce(
      (next, key) => {
        if (
          [
            PERSON_FIELDS_TO_SEND.isPatronymicAbsent,
            PERSON_FIELDS_TO_SEND.patronymic,
            PERSON_FIELDS_TO_SEND.isNameChanged,
          ].includes(key)
        )
          return next;

        if (key === PERSON_FIELDS_TO_SEND.birthdate) {
          return {
            ...next,
            [PERSON_FIELD_NAME[key]]: setValue(
              formatDateFromDraft(personFields[key])
            ),
          };
        }
        if (key === PERSON_FIELDS_TO_SEND.isRegisteredInMoscow) {
          return {
            ...next,
            isRegisteredInMoscow: Boolean(personFields[key]),
          };
        }

        if (key === PERSON_FIELDS_TO_SEND.changeName) {
          if (!personFields[key]) return next;

          const changedPatronymic = patronymicFromDraft(
            personFields[PERSON_FIELDS_TO_SEND.changeName],
            CHANGED_FIO_FIELD_NAME.changedPatronimic,
            IS_CHANGED_PATRONYMIC_ABSENT
          );

          const newField = {
            [CHANGED_FIO_FIELD_NAME.changedName]: setValue(
              personFields[PERSON_FIELDS_TO_SEND.changeName][
                PERSON_FIELDS_TO_SEND.name
              ]
            ),
            [CHANGED_FIO_FIELD_NAME.changedSurname]: setValue(
              personFields[PERSON_FIELDS_TO_SEND.changeName][
                PERSON_FIELDS_TO_SEND.surname
              ]
            ),
            ...changedPatronymic,
            [CHANGE_NAME_DOC_ISSUER]: setValue(
              personFields[PERSON_FIELDS_TO_SEND.changeName][
                PERSON_FIELDS_TO_SEND.changeNameDocIssuer
              ]
            ),
          };
          return { ...next, ...newField };
        }
        return {
          ...next,
          [PERSON_FIELDS_FROM_DRAFT[key]]: setValue(personFields[key]),
        };
      },
      { ...patronymic, [PERSON_FIELD_NAME.changeName]: setValue(changeName) }
    );
};

const marriageDataFromDraft: (
  marriageData: MarriageForSubmit
) => Partial<ApplicantFields> | null = (marriageData) => {
  if (!marriageData) return null;
  return Object.keys(marriageData).reduce((next, key) => {
    if (!marriageData[key]) return next;
    if (key === MARRIAGE_FIELDS_TO_SEND.dateOfMarriage) {
      return {
        ...next,
        [MARRIAGE_FIELDS_FROM_DRAFT[key]]: setValue(
          formatDateFromDraft(marriageData[key])
        ),
      };
    }
    return {
      ...next,
      [MARRIAGE_FIELDS_FROM_DRAFT[key]]: setValue(marriageData[key]),
    };
  }, {});
};

export const documentFromDraft: (
  values?,
  defaultDulType?: DOCUMENT_TYPE | null
) => Partial<DocumentFields> | null = (
  values,
  defaultDulType = DOCUMENT_TYPE.PASSPORT_RF
) => {
  if (!values) return null;

  return Object.keys(values)
    .filter(isDocumentFields)
    .reduce(
      (next, key) => {
        if (!values[key]) return next;
        if (key === DOCUMENT_FIELDS_TO_SEND.type) {
          const docType = DOCUMENT_TYPE[values[key]];
          const isBC = [
            DOCUMENT_TYPE.BIRTH_CERTIFICATE_RF,
            DOCUMENT_TYPE.BIRTH_CERTIFICATE_INTERNATIONAL,
          ].includes(+DOCUMENT_TYPE[values[key]]);
          return {
            ...next,
            [DOCUMENT_FIELDS_TO_SEND.type]: setValue(docType),
            birthCertificate: isBC,
          };
        }
        if (key === DOCUMENT_FIELDS_TO_SEND.issueDate) {
          return {
            ...next,
            [DOCUMENT_FIELDS_FROM_DRAFT[key]]: setValue(
              formatDateFromDraft(values[key])
            ),
          };
        }
        return {
          ...next,
          [DOCUMENT_FIELDS_FROM_DRAFT[key]]: setValue(values[key]),
        };
      },
      {
        ...documentInitialState,
        type: {
          value: defaultDulType,
          error: false,
          disabled: false,
        },
        birthCertificate: false,
      }
    );
};

export const applicantFromDraft = (obj) => {
  const { document: dulFields, ...person } = obj;
  const personData = personFromDraft(person);
  const citizenship = citizenshipFromDraft(dulFields);
  const document = documentFromDraft(dulFields, null);
  const birthPlace = birthPlaceFromDraft(dulFields);
  return Object.keys(person)
    .filter(isApplicantFields)
    .reduce(
      (next, key) => {
        if (key === APPLICANT_FIELDS_TO_SEND.phone) {
          const value = person[key];
          return {
            ...next,
            [APPLICANT_FIELDS_TO_SEND.phone]: setValue(
              value ? serializePhoneValue(value) : ''
            ),
          };
        }
        if (key === APPLICANT_FIELDS_TO_SEND.livingTogether) {
          const getType = (value) => {
            if (typeof value !== 'boolean') return null;
            return value
              ? LIVING_TOGETHER.livingTogether
              : LIVING_TOGETHER.notLivingTogether;
          };
          return {
            ...next,
            [MARRIAGE_INFO_FIELD_NAME.livingTogether]: setValue(
              getType(person[key])
            ),
          };
        }
        if (key === APPLICANT_FIELDS_TO_SEND.married) {
          const getType = (value) => {
            if (typeof value !== 'boolean') return null;
            return value ? MARITAL_STATUS.married : MARITAL_STATUS.notMarried;
          };
          return {
            ...next,
            [MARRIAGE_INFO_FIELD_NAME.married]: setValue(getType(person[key])),
          };
        }
        return {
          ...next,
          [APPLICANT_FIELDS_TO_SEND[key]]: setValue(person[key]),
        };
      },
      { ...personData, citizenship, document, birthPlace }
    );
};

export const spouseFromDraft = (spouseFields) => {
  if (!spouseFields) return null;
  const {
    address: addressFields,
    document: dulFields,
    ...personFields
  } = spouseFields;

  const getAddressRelativeFields = (addressFields) => {
    const addressMatchWithApplicant =
      addressFields[ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant];
    const address =
      addressMatchWithApplicant === ADDRESS_MATCH.match
        ? null
        : addressFromDraft(addressFields);
    return {
      address,
      addressMatchWithApplicant: {
        value:
          typeof addressMatchWithApplicant === 'string'
            ? addressMatchWithApplicant
            : null,
      },
    };
  };

  const address = getAddressRelativeFields(addressFields);
  const document = documentFromDraft(dulFields, null);
  const birthPlace = birthPlaceFromDraft(dulFields);
  const citizenship = citizenshipFromDraft(dulFields);

  return {
    ...personFromDraft(personFields),
    ...address,
    document,
    birthPlace,
    citizenship,
  };
};

const organizationFromDraft: (fields: Organization) => Partial<Organization> = (
  fields
) => {
  if (!fields) return { ...initialOrganizationState };

  return Object.keys(fields).reduce((next, key) => {
    if (key === ORGANIZATION_FIELDS_TO_SEND.isFoundInCatalog) {
      return {
        ...next,
        ...{ [ORGANIZATION_FIELD_NAME.isFoundInCatalog]: fields[key] },
      };
    }
    if (key === ORGANIZATION_FIELDS_TO_SEND.institutionName) {
      return {
        ...next,
        ...{ [ORGANIZATION_FIELD_NAME.institutionName]: setValue(fields[key]) },
      };
    }
    return next;
  }, {});
};

export const childAddressFromDraft = (
  childAddress: AddressFieldsForSubmit,
  isAdultChild: boolean
): {
  address: AddressFields;
  addressMatchWithApplicant: CHILD_ADDRESS;
} => {
  if (isAdultChild) {
    return {
      address: initialAddressInfo,
      addressMatchWithApplicant: setValue(null),
    };
  }
  if (!childAddress) {
    return {
      address: initialAddressInfo,
      addressMatchWithApplicant: setValue(null),
    };
  }
  const addressMatchWithApplicant =
    childAddress[ADDRESS_FIELDS_TO_SEND.addressMatchWithApplicant];
  if (addressMatchWithApplicant === CHILD_ADDRESS.matchWithApplicant) {
    return {
      address: initialAddressInfo,
      addressMatchWithApplicant: setValue(CHILD_ADDRESS.matchWithApplicant),
    };
  }
  if (addressMatchWithApplicant === CHILD_ADDRESS.matchWithSpouse) {
    return {
      address: initialAddressInfo,
      addressMatchWithApplicant: setValue(CHILD_ADDRESS.matchWithSpouse),
    };
  }
  const addressFields = addressFromDraft(childAddress);

  const isAddressCompleted = addressFields
    ? checkAddress(addressFields as AddressFields)
    : false;

  return {
    address: { ...initialAddressInfo, ...addressFields, isAddressCompleted },
    addressMatchWithApplicant: setValue(addressMatchWithApplicant),
  };
};

export const secondParentFromDraft = (secondParentFields) => {
  if (!secondParentFields) return { ...initialSecondParentState };
  const personFields = deepmerge(
    initialSecondParentState,
    personFromDraft(secondParentFields)
  );
  const id = getFullName(personFields);

  if (secondParentFields.is_single_parent) {
    return {
      ...initialSecondParentState,
      id: getId(),
      status: setValue(SECOND_PARENT_STATUS.single),
    };
  }
  if (secondParentFields.have_death_certificate) {
    return {
      ...personFields,
      id,
      proofOfDeath: setValue(DEAD_PARENT.hasDeathCert),
      status: setValue(SECOND_PARENT_STATUS.widowed),
      deathdate: setValue(
        formatDateFromDraft(
          secondParentFields[SECOND_PARENT_FIELDS_TO_SEND.deathdate]
        )
      ),
      deathDocIssuer: setValue(
        secondParentFields[SECOND_PARENT_FIELDS_TO_SEND.deathDocIssuer]
      ),
    };
  }
  if (secondParentFields.have_death_court_decision) {
    return {
      ...personFields,
      id,
      proofOfDeath: setValue(DEAD_PARENT.courtDecision),
      status: setValue(SECOND_PARENT_STATUS.widowed),
    };
  }

  if (secondParentFields.is_paternity_established) {
    return {
      ...initialSecondParentState,
      id: getId(),
      status: setValue(SECOND_PARENT_STATUS.paternity),
      paternityRegisterDate: setValue(
        formatDateFromDraft(
          secondParentFields[SECOND_PARENT_FIELDS_TO_SEND.paternityRegisterDate]
        )
      ),
      paternityRegisterNumber: setValue(
        secondParentFields[SECOND_PARENT_FIELDS_TO_SEND.paternityRegisterNumber]
      ),
    };
  }
  if (secondParentFields.is_marriage_terminated) {
    return {
      ...personFields,
      id,
      status: setValue(SECOND_PARENT_STATUS.divorced),
      dateOfDivorce: setValue(
        formatDateFromDraft(
          secondParentFields[SECOND_PARENT_FIELDS_TO_SEND.dateOfDivorce]
        )
      ),
      dateOfMarriage: setValue(
        formatDateFromDraft(
          secondParentFields[SECOND_PARENT_FIELDS_TO_SEND.dateOfMarriage]
        )
      ),
      divorceDocIssuer: setValue(
        secondParentFields[SECOND_PARENT_FIELDS_TO_SEND.divorceDocIssuer]
      ),
    };
  }
};

export const childFromDraft = (childFields) => {
  const {
    address: addressFields,
    document: dulFields,
    birthCertificate: birthCertificateFields,
    education: educationFields,
    polyclinic: polyclinicFields,
    second_parent: secondParentFields,
    is_studying: isStudying,
    birthdate,
    id,
    ...personFields
  } = childFields;

  const birthDate = formatDateFromDraft(birthdate);
  const shouldHaveDul = shouldHaveIdentification(birthDate);
  const isAdultChild = isAdult(birthDate);

  const addressRelativeFields = childAddressFromDraft(
    addressFields,
    isAdultChild
  );
  const dulFromDraft = documentFromDraft(dulFields);
  const emptyDulData = shouldHaveDul && !dulFromDraft;
  const dul = emptyDulData
    ? {
        ...dulInitialState,
        type: { value: 1, error: false, disabled: true },
      }
    : dulFromDraft;
  const cardStatus = cardInitialState;

  const birthPlace = birthPlaceFromDraft(birthCertificateFields);
  const citizenship = citizenshipFromDraft(birthCertificateFields);
  const birthCertificate = documentFromDraft(birthCertificateFields);
  const person = personFromDraft(personFields);
  const fullName = getFullName(person);
  const educationData = organizationFromDraft(educationFields);
  const polyclinic = { polyclinic: organizationFromDraft(polyclinicFields) };
  const education = { education: educationData };
  const secondParent = secondParentFromDraft(secondParentFields);

  const childData = {
    id,
    ...initialPersonState,
    ...person,
    birthdate: setValue(birthDate),
    changeName: person[PERSON_FIELD_NAME.changeName] || {
      value: CHANGE_NAME.match,
    },
    fullName,
    dul,
    birthCertificate,
    birthPlace,
    citizenship,
    ...education,
    ...polyclinic,
    isStudying: typeof isStudying === 'boolean' ? isStudying : null,
    secondParent,
    hidden: false,
    selectedParent: { value: getFullName(secondParent) },
    showSecondParentContent: false,
    cardStatus,
    ...addressRelativeFields,
  };
  const getBornInCurrentMarriage = (value) => {
    if (typeof value === 'boolean') {
      return value
        ? setValue(BORN_IN.currentMarriage)
        : setValue(BORN_IN.notCurrentMarriage);
    }
    return setValue('');
  };

  return Object.keys(personFields)
    .filter(isChildFields)
    .reduce((next, key) => {
      if (key === CHILD_FIELDS_TO_SEND.bornInCurrentMarriage) {
        return {
          ...next,
          bornInCurrentMarriage: getBornInCurrentMarriage(personFields[key]),
        };
      }
      if (key === CHILD_FIELDS_TO_SEND.confirmingDocs) {
        return {
          ...next,
          confirmingDocs: setValue(personFields[key]),
        };
      }
      if (personFields[CHILD_FIELDS_TO_SEND.isApplicantChild]) {
        return {
          ...next,
          bornInNotCurrentMarriage: setValue(BORN_IN_MARRIAGE.ofApplicant),
        };
      }
      if (personFields[CHILD_FIELDS_TO_SEND.isSpouseChild]) {
        return {
          ...next,
          bornInNotCurrentMarriage: setValue(BORN_IN_MARRIAGE.ofSpouse),
        };
      }

      return next;
    }, childData);
};

const agreementsFromDraft = ({
  warnedAboutResponsibility,
  consentWithTerms,
}) => {
  return {
    warnedAboutResponsibility: { checked: !!warnedAboutResponsibility },
    consentWithTerms: { checked: !!consentWithTerms },
  };
};

export const transformDataFromDraft = (data) => {
  const {
    applicant: applicantFields,
    spouse: spouseFields,
    children,
    warned_about_responsibility: warnedAboutResponsibility,
    consent_with_terms: consentWithTerms,
  } = data;
  const applicantAddress = addressFromDraft(applicantFields.address);
  const hasChildren = !!children && children.length;

  const marriageData = marriageDataFromDraft(applicantFields.marriage);
  const applicant = {
    ...applicantFromDraft(applicantFields),
    address: applicantAddress,
    ...marriageData,
  };

  const spouse = spouseFromDraft(spouseFields);

  const childrenArray = hasChildren
    ? children.map((child) => childFromDraft(child))
    : [];

  const agreements = agreementsFromDraft({
    warnedAboutResponsibility,
    consentWithTerms,
  });
  return { applicant, spouse, children: childrenArray, agreements };
};
